import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, CheckCircle, IdentificationCard, Sparkle } from "@phosphor-icons/react";
import { passwordError, PASSWORD_HINT } from "../lib/password";

export default function SetupAccount() {
  const navigate = useNavigate();
  const { token } = useParams();
  const { setUser } = useAuth();
  const [greeting, setGreeting] = useState(null); // {email, name, role, company_name, company_plan}
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/setup/${token}`);
        setGreeting(data);
      } catch (e) {
        setInvalid(formatApiError(e?.response?.data?.detail) || "This link is invalid or has expired.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const pe = passwordError(pw);
    if (pe) return setErr(pe);
    if (pw !== pw2) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/setup/${token}`, { new_password: pw });
      if (data.access_token) localStorage.setItem("hrms_token", data.access_token);
      setUser(data.user);
      setDone(true);
      setTimeout(() => navigate("/app", { replace: true }), 1500);
    } catch (ex) {
      setErr(formatApiError(ex?.response?.data?.detail) || "Could not set your password. The link may have expired.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-zinc-100" data-testid="setup-root">
      <div className="flex flex-col justify-between p-8 lg:p-14 bg-zinc-950 text-white">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="setup-back">
          <ArrowLeft size={16} /> Sign in instead
        </Link>
        <div>
          <div className="inline-flex items-center gap-2 mb-8">
            <div className="w-9 h-9 bg-white text-zinc-950 flex items-center justify-center rounded-sm">
              <IdentificationCard weight="fill" size={20} />
            </div>
            <div className="font-display font-black text-xl">SaarOne</div>
          </div>
          <h1 className="font-display font-black text-4xl lg:text-5xl leading-tight tracking-tight">
            {greeting?.company_name ? <>Welcome to<br/>{greeting.company_name}.</> : "Set up your account."}
          </h1>
          <p className="text-zinc-400 mt-6 text-lg max-w-md leading-relaxed">
            Pick a password — we'll sign you in and drop you straight into your workspace. No second step.
          </p>
          {greeting?.company_plan && (
            <div className="mt-8 inline-flex items-center gap-2 border border-zinc-700 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest text-zinc-300">
              <Sparkle size={12} weight="fill" /> {greeting.company_plan} plan
            </div>
          )}
        </div>
        <div className="text-xs tracking-widest uppercase text-zinc-500">© 2026 SaarOne</div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-14">
        <div className="w-full max-w-md">
          {loading && (
            <div className="text-center text-zinc-500" data-testid="setup-loading">Verifying your invite…</div>
          )}

          {!loading && invalid && (
            <div className="text-center" data-testid="setup-invalid">
              <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 text-red-600 mx-auto flex items-center justify-center text-2xl">!</div>
              <h2 className="font-display font-bold text-2xl mt-4">This invite link can't be used.</h2>
              <p className="text-sm text-zinc-600 mt-3 max-w-sm mx-auto">{invalid}</p>
              <p className="text-xs text-zinc-500 mt-4">Ask your admin to click "Resend invite" — a fresh link (valid 14 days) will land in your inbox. Already set a password? Just sign in.</p>
              <Link to="/login" className="inline-block mt-6 text-sm font-medium underline">Back to sign in</Link>
            </div>
          )}

          {!loading && !invalid && done && (
            <div className="text-center" data-testid="setup-success">
              <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto"/>
              <h2 className="font-display font-bold text-2xl mt-4">All set!</h2>
              <p className="text-sm text-zinc-500 mt-2">Taking you to your workspace…</p>
            </div>
          )}

          {!loading && !invalid && !done && greeting && (
            <>
              <div className="text-xs uppercase tracking-widest text-zinc-500">Set your password</div>
              <h2 className="font-display font-bold text-3xl mt-2">Hi {greeting.name?.split(" ")[0] || "there"}.</h2>
              <p className="text-sm text-zinc-600 mt-2">
                You're signing in as <span className="font-mono text-zinc-900">{greeting.email}</span>{greeting.role && <> — role: <span className="uppercase tracking-wider text-[11px] font-medium">{greeting.role.replace(/_/g, " ")}</span></>}.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="setup-form">
                <div>
                  <Label htmlFor="pw" className="text-xs uppercase tracking-wider font-medium text-zinc-600">New password</Label>
                  <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={10} autoFocus
                    className="mt-2 h-11 bg-white border-zinc-300 rounded-md" data-testid="setup-pw-input"/>
                  <p className="mt-1.5 text-[11px] text-zinc-500" data-testid="setup-pw-hint">{PASSWORD_HINT}</p>
                </div>
                <div>
                  <Label htmlFor="pw2" className="text-xs uppercase tracking-wider font-medium text-zinc-600">Confirm password</Label>
                  <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={10}
                    className="mt-2 h-11 bg-white border-zinc-300 rounded-md" data-testid="setup-pw2-input"/>
                </div>
                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="setup-error">{err}</div>}
                <Button type="submit" disabled={busy} className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 rounded-md" data-testid="setup-submit">
                  {busy ? "Setting up…" : "Set password & sign in"}
                </Button>
                <p className="text-[11px] text-zinc-500 text-center">By continuing you agree to our <Link to="/legal/terms" className="underline">Terms</Link> and <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.</p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
